import React, { useMemo, useState } from 'react'
import UnexpectedErrorBoundary from 'components/UnexpectedErrorBoundary'
import { Widget } from 'features/types'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import { InsightCount } from 'schemas/action/InsightCountSchema'
import { CountType, MessageConst } from 'enums'
import { SocialWebAuthorsWidget as SocialWebAuthorsWidgetType } from 'schemas/SocialWebAuthorsWidgetSchema'
import { getLabel } from 'utils/widgetTypeUtil'
import Table from '../share/Table'
import AuthorsWidgetDataGuards from '../share/guards/AuthorsWidgetDataGuards'
import { convertToTableData, getNumberFormatter } from '../share/Table/utils'
import InsightModal from '../share/InsightModal'
import useSelectedInsights from '../hooks'

const SocialWebAuthorsWidget: Widget = ({ widgetId }) => {
  const widgetSetting = useDashboardWidgetSetting<SocialWebAuthorsWidgetType>(widgetId)
  const widgetData = useDashboardWidgetData<InsightCount>(widgetId)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const countType = widgetSetting.countType || CountType.MENTIONS
  const {
    selectedInsights,
    detailSetting,
    handleRowClick,
    widgetSettingWithSelection
  } = useSelectedInsights(isModalOpen, widgetSetting, setIsModalOpen)

  const rowData = useMemo(() => convertToTableData(widgetData?.data, widgetSetting), [
    widgetData,
    widgetSetting
  ])

  const handleMentionsDisplay = (mentions: number | undefined) => {
    if (mentions) {
      return getNumberFormatter().format(mentions)
    }
    if (mentions === 0) {
      return MessageConst.ONE_PLUS
    }
    return MessageConst.EM_DASH
  }

  return (
    <UnexpectedErrorBoundary>
      <AuthorsWidgetDataGuards widgetId={widgetId} countType={countType}>
        <Table
          rowData={rowData}
          onMentionsHandle={handleMentionsDisplay}
          countType={countType}
          onRowClick={handleRowClick}
        />
      </AuthorsWidgetDataGuards>
      <InsightModal
        widgetSettingWithSelection={widgetSettingWithSelection}
        open={isModalOpen}
        title={widgetSetting?.name || getLabel(widgetSetting?.type)}
        countType={countType}
        selectedInsights={selectedInsights}
        onClose={() => {
          setIsModalOpen(false)
        }}
        detailSetting={detailSetting}
      />
    </UnexpectedErrorBoundary>
  )
}

export default SocialWebAuthorsWidget
